import { AxiosRequestConfig, AxiosResponse } from 'axios';
import api from '../services/url';
import { getSessionVariable, updateSessionVariableValue } from '../utils/utils';
import { cookieNames } from '../constants/constants';

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      // Check if the response contains the X-Token-Refreshed header
      // Try different case variations for better compatibility
      const tokenRefreshed = response.headers['x-token-refreshed'] || 
                            response.headers['X-Token-Refreshed'] ||
                            response.headers['X-TOKEN-REFRESHED'];
      
      console.log('Response headers:', response.headers);
      console.log('Response headers keys:', Object.keys(response.headers));
      console.log('X-Token-Refreshed header value:', tokenRefreshed);
      
      // Debug: Check if the header exists in different ways
      console.log('Direct access x-token-refreshed:', response.headers['x-token-refreshed']);
      console.log('Direct access X-Token-Refreshed:', response.headers['X-Token-Refreshed']);
      console.log('Direct access X-TOKEN-REFRESHED:', response.headers['X-TOKEN-REFRESHED']);
      
      // Check if it's in the raw headers
      console.log('Raw headers:', response.headers);
      console.log('Headers type:', typeof response.headers);
      console.log('Headers constructor:', response.headers.constructor.name);
      
      // Debug: Check all headers that contain 'token' or 'refresh'
      const tokenRelatedHeaders = Object.keys(response.headers).filter(key => 
        key.toLowerCase().includes('token') || key.toLowerCase().includes('refresh')
      );
      console.log('Token-related headers:', tokenRelatedHeaders);
      
      // Check each token-related header
      tokenRelatedHeaders.forEach(header => {
        console.log(`${header}:`, response.headers[header]);
      });
      
      // Also check for any header that might be the token refresh header
      const allHeaders = Object.keys(response.headers);
      console.log('All response headers:', allHeaders);
      
      // Look for headers that might be the token refresh header (case insensitive)
      const possibleTokenRefreshHeaders = allHeaders.filter(key => 
        key.toLowerCase().includes('x-token-refreshed') || 
        key.toLowerCase().includes('token-refreshed') ||
        key.toLowerCase().includes('refreshed')
      );
      console.log('Possible token refresh headers:', possibleTokenRefreshHeaders);
      
      // Also check response body for token refresh indicators
      const hasRefreshedToken = response.data?.refreshedToken || response.data?.tokenRefreshed;
      
      // Debug: Log response data structure
      console.log('Response data:', response.data);
      console.log('Has refreshed token in body:', hasRefreshedToken);
      
      // Additional fallback: check if any of the possible headers contain 'true'
      const anyTokenRefreshHeader = possibleTokenRefreshHeaders.find(header => 
        response.headers[header] === 'true' || response.headers[header] === true
      );
      
      const finalTokenRefreshed = tokenRefreshed || anyTokenRefreshHeader;
      
      if (finalTokenRefreshed === 'true' || finalTokenRefreshed === true || hasRefreshedToken) {
        console.log('Token was refreshed by backend');
        console.log('Final token refreshed value:', finalTokenRefreshed);
        
        // Get the new token from the response
        const newToken = response.data?.refreshedToken || response.data?.authorizationToken;
        
        if (newToken && newToken !== '') {
          // Buyer session is 24 hours (SOCIETYPORTAL); match the backend JWT life.
          const expirationTime = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours
          updateSessionVariableValue(cookieNames.jwtToken, newToken, expirationTime);
          updateSessionVariableValue(cookieNames.tokenExpiryTime, expirationTime.getTime(), expirationTime);

          console.log('Token refreshed by backend - updated with 24-hour expiry');
        } else if (finalTokenRefreshed === 'true' || finalTokenRefreshed === true) {
          // Header indicates refresh but no new token - extend session anyway
          console.log('Token refresh header detected but no new token - extending session');
          const expirationTime = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours
          updateSessionVariableValue(cookieNames.tokenExpiryTime, expirationTime.getTime(), expirationTime);
          console.log('Session extended by 24 hours due to token refresh header');
        } else {
          console.warn('Token refresh detected but no new token in response');
        }
      }
    } catch (error) {
      console.error('Error in response interceptor:', error);
    }
    
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const useCustomAxios = () => {
  const authToken = getSessionVariable(cookieNames.jwtToken);

  const defaultConfig = {
    headers: {
      "x-source": "SOCIETYPORTAL",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
    },
  };

  const getConfig = (customConfig: AxiosRequestConfig | undefined) => {
    return customConfig ? { ...defaultConfig, ...customConfig } : defaultConfig;
  };

  const axiosGet = async (url: string, config?: AxiosRequestConfig) => api.get(url, { ...getConfig(config) });

  const axiosPost = async (url: string, postBody: { [key: string]: any }, config?: AxiosRequestConfig) => api.post(url, postBody, { ...getConfig(config) });

  const axiosPut = async (url: string, putBody: { [key: string]: any }, config?: AxiosRequestConfig) => api.put(url, putBody, { ...getConfig(config) });

  const axiosPatch = async (url: string, patchBody: { [key: string]: any }, config?: AxiosRequestConfig) => api.patch(url, patchBody, { ...getConfig(config) });

  const axiosDelete = async (url: string, deleteBody: { [key: string]: any }) => api.put(url, deleteBody);

  return { axiosGet, axiosPost, axiosPut, axiosPatch, axiosDelete };
}; 